import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { APIResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';

import {
  MfaEmailSendResult,
  MfaEnrollResult,
  MfaMethod,
  MfaPolicySettings,
  MfaPolicyUpdate,
  MfaRecoveryCodesResult,
  MfaRecoveryState,
  MfaResetResult,
  MfaStatus,
} from '../models/mfa.models';

/**
 * The authenticated MFA surface. The login-side challenge endpoints answer
 * with a session and live on `AuthService` instead. Stateless: touches no
 * storage.
 */
@Injectable({ providedIn: 'root' })
export class MfaService {
  constructor(private readonly http: HttpClient) {}

  status(): Observable<APIResponse<MfaStatus>> {
    return this.http.get<APIResponse<MfaStatus>>(`${environment.BASE_URL_API}auth/mfa`);
  }

  /** TOTP replaces a pending factor; over an active one it 409s. */
  enroll(method: MfaMethod): Observable<APIResponse<MfaEnrollResult>> {
    return this.http.post<APIResponse<MfaEnrollResult>>(
      `${environment.BASE_URL_API}auth/mfa/enroll`,
      { method }
    );
  }

  confirm(method: MfaMethod, code: string): Observable<APIResponse<MfaStatus>> {
    return this.http.post<APIResponse<MfaStatus>>(
      `${environment.BASE_URL_API}auth/mfa/confirm`,
      { method, code }
    );
  }

  /** Throttled server-side to 60s / 5 per hour. */
  resendEnrolmentCode(): Observable<APIResponse<MfaEmailSendResult>> {
    return this.http.post<APIResponse<MfaEmailSendResult>>(
      `${environment.BASE_URL_API}auth/mfa/email/send`,
      { purpose: 'enroll' }
    );
  }

  /**
   * Mail a code for a *step-up*, not an enrolment.
   *
   * Same endpoint and the same `purpose` as the enrolment resend on purpose:
   * `_verify_active_factor` checks step-up codes against the 'enroll'
   * purpose, so a code sent under any other would be refused. Named
   * separately because the call sites mean different things.
   */
  sendStepUpCode(): Observable<APIResponse<MfaEmailSendResult>> {
    return this.resendEnrolmentCode();
  }

  /**
   * Step-up gated while another factor is active. `code` goes in the body --
   * a query parameter would put an OTP in the access logs.
   */
  disable(method: MfaMethod, code?: string): Observable<APIResponse<MfaStatus>> {
    return this.http.request<APIResponse<MfaStatus>>(
      'DELETE',
      `${environment.BASE_URL_API}auth/mfa`,
      { body: code ? { method, code } : { method } }
    );
  }

  /**
   * Issues a batch of recovery codes, retiring any still outstanding.
   *
   * Step-up gated like `disable`: each code permanently bypasses the second
   * factor, so an access token alone must not mint them. 409 until a factor is
   * active. The response is the only copy that will exist.
   */
  generateRecoveryCodes(code?: string): Observable<APIResponse<MfaRecoveryCodesResult>> {
    return this.http.post<APIResponse<MfaRecoveryCodesResult>>(
      `${environment.BASE_URL_API}auth/mfa/recovery-codes`,
      // Body, never a query parameter: that would log a live code.
      code ? { code } : {}
    );
  }

  /**
   * Needs `edit:settings_security`. Removes every factor and bumps the
   * target's token epoch, so it also ends all their sessions.
   */
  resetUserMfa(userId: number): Observable<APIResponse<MfaResetResult>> {
    return this.http.post<APIResponse<MfaResetResult>>(
      `${environment.BASE_URL_API}users/${userId}/mfa/reset`,
      {}
    );
  }

  /** Tenant-wide policy. Needs `access:settings_security`. */
  policySettings(): Observable<APIResponse<MfaPolicySettings>> {
    return this.http.get<APIResponse<MfaPolicySettings>>(
      `${environment.BASE_URL_API}settings/security`
    );
  }

  /**
   * Needs `edit:settings_security`. Partial: keys absent from `patch` are
   * left as they are. Answers 409 on `required` when the caller holds no
   * factor of their own.
   */
  updatePolicySettings(
    patch: MfaPolicyUpdate
  ): Observable<APIResponse<MfaPolicySettings>> {
    return this.http.patch<APIResponse<MfaPolicySettings>>(
      `${environment.BASE_URL_API}settings/security`,
      patch
    );
  }
}
